import React, { useEffect, useState } from "react";
import SelectField from "../../../../../../global components/Forms/SelectField/SelectField";
import s from "./Table.module.scss";
import SearchInput from "../../../../../../global components/Forms/SearchInput/SearchInput";
import ReusableTable from "../../../../../../global components/Table/Table";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import { UseAuthToken } from "../../../../../../libs/api/userSession";
import { PaginatedSentMessage } from "../../../../../../libs/Utils/Types/Message";
import useSWR from "swr";
import {
  parseClientError,
  swrFetcher,
} from "../../../../../../libs/api/Endpoints";
import dayjs from "dayjs";

import Envelope from "../../../../../../global components/Icons/Envelope/Envelope";
import clsx from "clsx";
import Paginate from "../../../../../../global components/Paginate/Pagination";
import { useNavigate } from "react-router-dom";
import Unauthorised from "../../../../../../global components/Unauthorised";
import CsvDownloader from "../CsvDownloader";

type prop = {
  filters: "" | "sent" | "pending" | "failed" | "scheduled";
};
function Table({ filters }: prop) {
  const token = UseAuthToken();
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageItems, setPageItems] = useState(10);
  const [filter, setFilter] = useState<string>("");
  const navigate = useNavigate();
  const [unauthorised, setUnauthorised] = useState<
    boolean | { title: string; message: string }
  >(false);
  const {
    data: TableContacts,
    isLoading,
    error,
  } = useSWR<PaginatedSentMessage, any, any>(
    token
      ? [
          "api/admin/messages",
          token,
          {
            page: currentPage,
            limit: pageItems,
            search: searchValue,
            ...(filter && { status: filter }),
          },
        ]
      : null,
    swrFetcher,
    {
      revalidateOnFocus: true, // Refetch when the window regains focus
      // Refetch every 10 seconds (10000 ms)
      shouldRetryOnError: false, // Disable retrying on errors
    },
  );

  // Define table columns
  const columns = [
    {
      title: "MESSAGE TITLE",
      dataIndex: "messageTitle",
      key: "messageTitle",
      render: (messageTitle: string) => (
        <span className={clsx(s.cell, s.messageTitle)}>{messageTitle}</span>
      ),
    },
    {
      title: "MESSAGE TYPE",
      dataIndex: "mesageType",
      key: "mesageType",
      render: (mesageType: string) => (
        <span className={clsx(s.cell)}>{mesageType.toUpperCase()}</span>
      ),
    },
    {
      title: "SENDER",
      dataIndex: "sender",
      key: "sender",
      render: (sender: string) => (
        <span className={clsx(s.cell)}>{sender}</span>
      ),
    },
    {
      title: "GROUPS",
      dataIndex: "group",
      key: "group",
      render: (group: string) => <span className={clsx(s.cell)}>{group}</span>,
    },
    {
      title: "CONTACTS",
      dataIndex: "contacts",
      key: "contacts",
      render: (contacts: string) => (
        <span className={clsx(s.cell)}>{contacts}</span>
      ),
    },

    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
      render: (date: string) => (
        <span className={s.cell}>
          {" "}
          {dayjs.utc(date).local().format("MMM D, YYYY hh:mm A")
}
        </span>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <span
          className={clsx(s.statusCell, s[status.toLowerCase()], {
            [s.pending]: status.toLowerCase() === "scheduled",
          })}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
    },
    {
      title: "OPEN RATE",
      dataIndex: "openRate",
      key: "openRate",
      render: (openRate: string) => (
        <span className={clsx(s.cell)}>{openRate}</span>
      ),
    },
    {
      title: "ACTIONS",
      dataIndex: "actions",
      key: "actions",
      render: (text: any, record: { key: string }) => (
        <h5
          className={s.view}
          onClick={() =>
            navigate(`/dashboard/Message/message-details?msgId=${record.key}`)
          }
        >
          View
        </h5>
      ),
    },
  ];
  const transformedData = TableContacts?.data.map((contact) => ({
    key: contact.id,
    messageTitle: contact.title || "N/A",
    mesageType: contact.type.replace("_", " ") || "N/A",
    group: contact.noOfContactGroups || "0",
    sender: contact.user
      ? (contact.user?.firstName || "") + " " + contact.user?.lastName || "N/A"
      : "N/A",
    contacts: contact.totalRecipients || 0,
    creditUsed: contact.totalCreditsUsed
      ? contact.totalCreditsUsed.toLocaleString()
      : 0,
    date: contact.sentAt,
    status: contact.status || "N/A",
    openRate: contact.deliveryRate ? contact.deliveryRate.toLocaleString() : 0,
    actions: "", // You can customize actions here as needed
  }));

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageItems(size);
  };
  function handleFilter(value: string) {
    setFilter(value);
  }
  const handleSearch = (value: string) => {
    setSearchValue(value);
    // You can add your search logic here, e.g., fetching data based on the search term
  };
  useEffect(() => {
    if (error) {
      const e = parseClientError(error);
      if (e.statusCode === 403) {
        setUnauthorised(true);
      } else {
        setUnauthorised({
          title: "Error",
          message:
            e.message.charAt(0).toUpperCase() + e.message.slice(1) ||
            "Something went wrong",
        });
      }
    }
  }, [error]);
  useEffect(() => {
    if (filters) {
      setFilter(filters);
    }
    if (filters === "") {
      setFilter("");
    }
  }, [filters]);
  return (
    <div className={s.wrapper}>
      <div className={s.tableWrapper}>
        <div className={s.formWrapper}>
          <div className={s.params}>
            <div className={s.searchInput}>
              <SearchInput
                style={{ width: "100%", height: "100%" }}
                placeholder="search"
                width={332}
                height={45}
                handleSearch={handleSearch}
              />
            </div>
            <SelectField
              bordered
              label={"Status:"}
              defaultValue={"all"}
              value={filter}
              wrapperClassname={s.SelectWrapper}
              className={clsx(s.selectInput)}
              onChange={(value: any) => handleFilter(value)}
              style={{ width: "100%", height: "100%" }}
              options={[
                { value: "", label: "All" }, // Option to reset the filter
                { value: "sent", label: "Sent" },
                { value: "pending", label: "Pending" },
                { value: "failed", label: "Failed" },
                { value: "scheduled", label: "Scheduled" },
              ]}
            />
          </div>
          <div className={s.params}>
            <Paginate
              onChange={handlePageChange}
              total={TableContacts?.pagination.totalItems || 0}
              pageSize={pageItems}
            />
            <CsvDownloader
              endpointKey="api/admin/messages"
              token={token!}
              filter={filter}
              filename="Messages"
              pageItems={pageItems}
            />
          </div>
        </div>
        <ReusableTable
          loading={isLoading}
          noDataComponent={
            unauthorised ? (
              typeof unauthorised === "boolean" ? (
                <Unauthorised />
              ) : (
                <Unauthorised error={unauthorised} />
              )
            ) : (
              <NoData
                Message="No message yet"
                subMessage="Get started by sending a message"
                icon={<Envelope fill="#F0EFEE" height={60} width={80} />}
              />
            )
          }
          dataSource={transformedData || []}
          columns={columns}
          //rowSelection={rowSelection} // Include row selection prop
        />
      </div>
    </div>
  );
}
export default Table;
